import { useState } from "react";
import { Search, FileText, Shield } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import * as tagsApi from "@/services/tagsApi";

interface TagProfilesTabProps { tagId: string; }

export const TagProfilesTab = ({ tagId }: TagProfilesTabProps) => {
  const [searchQuery, setSearchQuery] = useState("");

  const { data: policies = [], isLoading } = useQuery({
    queryKey: ["tag-policies", tagId],
    queryFn: () => tagsApi.getPoliciesByTag(tagId),
  });

  const filteredPolicies = policies.filter((p: any) =>
    (p.name || p.policyName || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.description || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i} className="animate-pulse"><CardHeader><div className="h-4 bg-muted rounded w-3/4"></div></CardHeader></Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input placeholder="Search profiles..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
        </div>
      </div>
      {filteredPolicies.length === 0 ? (
        <Card><CardContent className="flex flex-col items-center justify-center py-12">
          <FileText className="h-12 w-12 text-muted-foreground mb-4" />
          <CardTitle className="text-center mb-2">No Profiles Found</CardTitle>
          <CardDescription className="text-center">{searchQuery ? "No profiles match your search." : "No profiles assigned to this tag yet."}</CardDescription>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPolicies.map((policy: any) => {
            const policyId = policy.id || policy.policyId;
            const policyName = policy.name || policy.policyName || 'Unnamed Policy';
            const payloadType = policy.payloadType || policy.type;
            const platform = policy.platform;

            return (
              <Link key={policyId} to={`/configurations/${policyId}`} className="block">
                <Card className="hover:shadow-md transition-shadow h-full">
                  <CardHeader className="pb-3">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Shield className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-base truncate">{policyName}</CardTitle>
                        {policy.description && <CardDescription className="text-sm line-clamp-2 mt-1">{policy.description}</CardDescription>}
                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                          {payloadType && <Badge variant="secondary" className="text-[10px] h-4">{payloadType}</Badge>}
                          {platform && <Badge variant="outline" className="text-[10px] h-4 capitalize">{platform}</Badge>}
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};
